import { useCallback, useState } from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useS3Loader } from '@occc/feat-file-download';
import { snackbar } from '@occc-app/design-system';
import { ScanDataSubmissionFileType } from '@occc/graphql';

import { MetricType } from '../scan-data-overview.types';
import { useScanDataGenerateSubmissionCSV } from '../../../hooks/use-scan-data-generate-submission-csv.mutation.hook';

const fileTypeByMetricType: Record<MetricType, ScanDataSubmissionFileType> = {
  'stores-submitted': ScanDataSubmissionFileType.StoresSubmitted,
  'missing-stores': ScanDataSubmissionFileType.MissingStores,
  'transactions-reported': ScanDataSubmissionFileType.ReportedTransactions,
  reimbursements: ScanDataSubmissionFileType.Reimbursements,
};

const filenameByMetricType: Record<MetricType, string> = {
  'stores-submitted': 'stores-submitted',
  'missing-stores': 'missing-stores',
  'transactions-reported': 'transactions-reported',
  reimbursements: 'reimbursements',
};

const showError = () => {
  snackbar.error('Failed to download CSV file. Please try again.');
};

export const useScanDataDownloadSubmissionCSV = (
  selectedMetricType: MetricType,
  submissionDate: string,
) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [generateCSV] = useScanDataGenerateSubmissionCSV();

  const filename = `${filenameByMetricType[selectedMetricType]}-${submissionDate}.csv`;

  const { download } = useS3Loader({
    filename,
    onError: showError,
  });

  const handleDownloadCSV = useCallback(async () => {
    setIsDownloading(true);

    try {
      const fileType = fileTypeByMetricType[selectedMetricType];

      const result = await generateCSV({
        variables: {
          input: {
            fileType,
            submissionDate,
          },
        },
      });

      const s3Url = result.data?.scanDataGenerateSubmissionCSV;

      if (!s3Url) {
        throw new Error('No S3 URL returned from mutation');
      }

      await download(s3Url);
    } catch {
      showError();
    } finally {
      setIsDownloading(false);
    }
  }, [download, generateCSV, selectedMetricType, submissionDate]);

  return [handleDownloadCSV, isDownloading] as const;
};
